import React, { useState, useRef, useContext } from 'react';
import CompanySelector from './components/CompanySelector';
import FunctionAssignment from './FunctionAssignment';
import BulkGrabPushStoreInfo from './BulkGrabPushStoreInfo';
import DuplicateMacRemediation from './DuplicateMacRemediation';
import AllStoresSummary from './AllStoresSummary';
import './GlobalActionCenterStyles.css';
import ProtectedComponent from './ProtectedComponent';
import { AuthContext } from './context/AuthContext';
import { ACTION_CODES, ACTION_LABELS, ALL_GA_CODES, DESTRUCTIVE_GA_CODES, GAC_ENTRY_CODE } from './gac/capabilities';
import { notifyActivity } from './socket';
import { hasCode, userRank, rankOf } from './utils/appAccess';
import RegenerateImagesPanel, { RegenerateImagesResults } from './gac/RegenerateImagesPanel';
import BulkDeleteArticlesPanel from './gac/BulkDeleteArticlesPanel';
import BlinkLedPanel from './gac/BlinkLedPanel';
import GetAllLabelsPanel from './gac/GetAllLabelsPanel';
import PageFlipPanel, { PageFlipResults } from './gac/PageFlipPanel';
import AssignLabelPanel from './gac/AssignLabelPanel';

const GlobalActionCenter = () => {
    const { user } = useContext(AuthContext);

    // Per-action capability: holds the action's code AND, for destructive actions, is at least Admin.
    const canAction = (action) => {
        const code = ACTION_CODES[action];
        if (!code) return false;
        if (!hasCode(user, code)) return false;
        if (DESTRUCTIVE_GA_CODES.includes(code) && userRank(user) < rankOf('Admin')) return false;
        return true;
    };
    const anyCapability = Object.keys(ACTION_CODES).some((a) => a !== 'functionAssignment' && canAction(a));

    // Mode selection
    const [mode, setMode] = useState('byCompany'); // 'byCompany' or 'byStore'

    // Company and store data
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStores, setSelectedStores] = useState([]);
    const [companySearchFilter, setCompanySearchFilter] = useState(''); // For byCompany mode filtering

    // Action selection and configuration
    const [currentAction, setCurrentAction] = useState(''); // Start with no action selected

    // GLOBAL Company/Store selection (used by all functions)
    const [globalCompany, setGlobalCompany] = useState(null);
    const [globalStores, setGlobalStores] = useState([]);
    const [globalSelectedStore, setGlobalSelectedStore] = useState(null);
    const [globalStoreSearch, setGlobalStoreSearch] = useState('');
    const [showGlobalStoreDropdown, setShowGlobalStoreDropdown] = useState(false);
    const globalStoreInputRef = useRef(null);
    const globalStoreDropdownRef = useRef(null);
    const isSelectingGlobalStore = useRef(false);

    // Execution state
    const [executing, setExecuting] = useState(false);
    const [results, setResults] = useState(null);
    const [errors, setErrors] = useState([]);

    // Handle mode change
    const handleModeChange = (newMode) => {
        setMode(newMode);
        setCurrentAction(''); // Reset action when mode changes
        setSelectedCompany(null);
        setCompanySearchFilter('');
        setStores([]);
        setSelectedStores([]);
        setResults(null);
        setErrors([]);
    };

    // Handle action change
    const handleActionChange = (action) => {
        setCurrentAction(action);
        // Report the selected tool so the active-users view shows what someone is
        // doing here, not just that they are on /global-action-center.
        notifyActivity(ACTION_LABELS[action] || null);
        setSelectedCompany(null);
        setCompanySearchFilter('');
        setStores([]);
        setSelectedStores([]);
        setResults(null);
        setErrors([]);
    };

    // Handle company search change (for byCompany mode)
    const handleCompanySearchChange = (searchText) => {
        setCompanySearchFilter(searchText);
    };

    // Handle company selection - receives company and stores from CompanySelector
    const handleCompanySelect = (company, stores) => {
        setSelectedCompany(company);
        setStores(stores || []);
        setSelectedStores([]);
        setResults(null);
        setErrors([]);
    };

    // Handle company clear
    const handleCompanyClear = () => {
        setSelectedCompany(null);
        setStores([]);
        setSelectedStores([]);
        setResults(null);
        setErrors([]);
    };

    // Get filtered store list for global dropdown
    const getFilteredGlobalStores = () => {
        if (!globalStoreSearch.trim()) return globalStores;
        const search = globalStoreSearch.toLowerCase();
        return globalStores.filter(s =>
            s.store?.toLowerCase().includes(search) ||
            s.storeName?.toLowerCase().includes(search)
        );
    };

    return (
        <ProtectedComponent functionCode={GAC_ENTRY_CODE} functionCodes={[GAC_ENTRY_CODE, ...ALL_GA_CODES]} functionName="Global Action Center">
            <div className="global-action-center">
            <div className="header">
                <h1>Global Action Center</h1>
                <p className="subtitle">Perform bulk operations across all Companies or Stores</p>
            </div>

            {/* Compact Top Bar: Mode + Action (Company comes later) */}
            <div className="compact-controls-bar">
                {/* Mode Selection - No label, buttons first */}
                <div className="mode-selector-compact">
                    <button
                        className={`mode-button-compact ${mode === 'byCompany' ? 'active' : ''}`}
                        onClick={() => handleModeChange('byCompany')}
                    >
                        By Company
                    </button>
                    <button
                        className={`mode-button-compact ${mode === 'byStore' ? 'active' : ''}`}
                        onClick={() => handleModeChange('byStore')}
                    >
                        By Store
                    </button>
                </div>

                {/* Action Dropdown - Always show, select action first */}
                <div className="control-group action-select-compact">
                    <select
                        value={currentAction}
                        onChange={(e) => handleActionChange(e.target.value)}
                        className="action-select-dropdown"
                    >
                        <option value="">-- Select Action --</option>
                        {canAction('functionAssignment') && <option value="functionAssignment" disabled={mode === 'byStore'}>⚙️ Function Assignment {mode === 'byStore' ? '(By Company Only)' : ''}</option>}
                        {canAction('assignProduct') && <option value="assignProduct" disabled={mode === 'byCompany'}>🏷️ Assign Product {mode === 'byCompany' ? '(By Store Only)' : ''}</option>}
                        {canAction('blinkLed') && <option value="blinkLed" disabled={mode === 'byCompany'}>💡 Blink LED {mode === 'byCompany' ? '(By Store Only)' : ''}</option>}
                        {canAction('getAllLabels') && <option value="getAllLabels" disabled={mode === 'byCompany'}>🔍 Get All Labels {mode === 'byCompany' ? '(By Store Only)' : ''}</option>}
                        {canAction('pageFlip') && <option value="pageFlip" disabled={mode === 'byCompany'}>📄 Page Flip {mode === 'byCompany' ? '(By Store Only)' : ''}</option>}
                        {canAction('regenerateImages') && <option value="regenerateImages" disabled={mode === 'byStore'}>🖼️ Regenerate Images {mode === 'byStore' ? '(By Company Only)' : ''}</option>}
                        {canAction('bulkDeleteArticles') && <option value="bulkDeleteArticles">🗑️ Bulk Delete Articles (CSV)</option>}
                        {canAction('bulkGrabPush') && <option value="bulkGrabPush" disabled={mode === 'byCompany'}>🔁 Bulk Grab &amp; Push Store Info {mode === 'byCompany' ? '(By Store Only)' : ''}</option>}
                        {canAction('duplicateMacRemediation') && <option value="duplicateMacRemediation">🧬 Duplicate MAC Remediation</option>}
                        {canAction('allStoresSummary') && <option value="allStoresSummary">🗂️ All Companies/All Stores Summary</option>}
                        <option value="storeConfig" disabled>🏪 Store Config (Coming Soon)</option>
                    </select>
                </div>

                {/* GLOBAL Company Selection - Show for actions that need company */}
                {currentAction && ['blinkLed', 'assignProduct', 'pageFlip', 'getAllLabels', 'duplicateMacRemediation', 'regenerateImages'].includes(currentAction) && (
                    <div className="company-select-compact">
                        <label className="company-label-compact">Company:</label>
                        <CompanySelector
                            selectedCompanyValue={globalCompany}
                            onCompanySelect={(company, stores) => {
                                setGlobalCompany(company);
                                setGlobalStores(stores || []);
                                setGlobalSelectedStore(null);
                                setGlobalStoreSearch('');
                                // Also update byStore mode's selectedCompany for pageFlip
                                if (currentAction === 'pageFlip') {
                                    handleCompanySelect(company, stores);
                                }
                            }}
                            onClear={() => {
                                setGlobalCompany(null);
                                setGlobalStores([]);
                                setGlobalSelectedStore(null);
                                setGlobalStoreSearch('');
                                if (currentAction === 'pageFlip') {
                                    handleCompanyClear();
                                }
                            }}
                            placeholder="Select company..."
                            autoFetchStores={true}
                            multiSelect={false}
                        />
                    </div>
                )}

                {/* GLOBAL Store Selection - Show for actions that need store */}
                {currentAction && ['blinkLed', 'assignProduct', 'getAllLabels', 'duplicateMacRemediation'].includes(currentAction) && globalCompany && (
                    <div className="store-select-compact">
                        <label className="company-label-compact">Store:</label>
                        <div className="global-store-dropdown-wrapper">
                            {globalSelectedStore ? (
                                <div className="store-selected-display compact">
                                    <span className="store-selected-text">
                                        {globalSelectedStore.store} - {globalSelectedStore.storeName}
                                    </span>
                                    <span
                                        className="store-clear-x"
                                        onClick={() => {
                                            setGlobalSelectedStore(null);
                                            setGlobalStoreSearch('');
                                            setTimeout(() => globalStoreInputRef.current?.focus(), 50);
                                        }}
                                    >×</span>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    ref={globalStoreInputRef}
                                    className="store-search-input compact"
                                    placeholder="Search stores..."
                                    value={globalStoreSearch}
                                    onChange={(e) => {
                                        setGlobalStoreSearch(e.target.value);
                                        setShowGlobalStoreDropdown(true);
                                    }}
                                    onFocus={() => setShowGlobalStoreDropdown(true)}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            if (!isSelectingGlobalStore.current) {
                                                setShowGlobalStoreDropdown(false);
                                            }
                                            isSelectingGlobalStore.current = false;
                                        }, 200);
                                    }}
                                />
                            )}
                            {showGlobalStoreDropdown && !globalSelectedStore && (
                                <div className="store-dropdown global-store-dropdown" ref={globalStoreDropdownRef}>
                                    {getFilteredGlobalStores().slice(0, 8).map(store => (
                                        <div
                                            key={store.store}
                                            className="store-dropdown-item"
                                            onMouseDown={() => { isSelectingGlobalStore.current = true; }}
                                            onClick={() => {
                                                setGlobalSelectedStore({ store: store.store, storeName: store.storeName });
                                                setGlobalStoreSearch('');
                                                setShowGlobalStoreDropdown(false);
                                                isSelectingGlobalStore.current = false;
                                            }}
                                        >
                                            <strong>{store.store}</strong> - {store.storeName}
                                        </div>
                                    ))}
                                    {globalStoreSearch && getFilteredGlobalStores().length === 0 && (
                                        <div className="store-dropdown-empty">No stores match</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Store Required Warning */}
                {currentAction && ['blinkLed', 'assignProduct'].includes(currentAction) && globalCompany && !globalSelectedStore && (
                    <div className="store-required-badge">
                        <span className="warning-badge">Store required</span>
                    </div>
                )}

                {/* Stores Loaded Badge - Only show in byStore mode for pageFlip */}
                {mode === 'byStore' && stores.length > 0 && currentAction === 'pageFlip' && (
                    <div className="control-group stores-badge-compact">
                        <span className="info-badge-compact">
                            ✓ {stores.length} stores
                        </span>
                    </div>
                )}

            </div>

            {/* Store Selection + Page Flip configuration (extracted panel) */}
            {currentAction === 'pageFlip' && canAction('pageFlip') && (
                <PageFlipPanel
                    mode={mode}
                    selectedCompany={selectedCompany}
                    stores={stores}
                    selectedStores={selectedStores}
                    setSelectedStores={setSelectedStores}
                    executing={executing}
                    setExecuting={setExecuting}
                    results={results}
                    setResults={setResults}
                    setErrors={setErrors}
                />
            )}

            {/* Action Tiles - Show when no action selected */}
            {!currentAction && (
                <div className="action-tiles-container">
                    {!anyCapability && (
                        <div className="action-tiles-section">
                            <p className="config-hint">You have no Global Action Center capabilities yet. Ask a super admin to grant the actions you need.</p>
                        </div>
                    )}

                    {/* By Company Actions (alphabetical) */}
                    <div className="action-tiles-section">
                        <h2>By Company</h2>
                        <div className="action-tiles-grid">
                            {canAction('functionAssignment') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byCompany'); handleActionChange('functionAssignment'); }}
                                >
                                    <span className="action-tile-icon">⚙️</span>
                                    <span className="action-tile-label">Function Assignment</span>
                                </div>
                            )}
                            {canAction('allStoresSummary') && (
                                <div
                                    className="action-tile"
                                    onClick={() => handleActionChange('allStoresSummary')}
                                >
                                    <span className="action-tile-icon">🗂️</span>
                                    <span className="action-tile-label">All Companies/All Stores Summary</span>
                                </div>
                            )}
                            {canAction('regenerateImages') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byCompany'); handleActionChange('regenerateImages'); }}
                                >
                                    <span className="action-tile-icon">🖼️</span>
                                    <span className="action-tile-label">Regenerate Images</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* By Store Actions (alphabetical) */}
                    <div className="action-tiles-section">
                        <h2>By Store</h2>
                        <div className="action-tiles-grid">
                            {canAction('assignProduct') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byStore'); handleActionChange('assignProduct'); }}
                                >
                                    <span className="action-tile-icon">🏷️</span>
                                    <span className="action-tile-label">Assign Product</span>
                                </div>
                            )}
                            {canAction('blinkLed') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byStore'); handleActionChange('blinkLed'); }}
                                >
                                    <span className="action-tile-icon">💡</span>
                                    <span className="action-tile-label">Blink LED</span>
                                </div>
                            )}
                            {canAction('pageFlip') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byStore'); handleActionChange('pageFlip'); }}
                                >
                                    <span className="action-tile-icon">📄</span>
                                    <span className="action-tile-label">Page Flip</span>
                                </div>
                            )}
                            {canAction('getAllLabels') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byStore'); handleActionChange('getAllLabels'); }}
                                >
                                    <span className="action-tile-icon">🔍</span>
                                    <span className="action-tile-label">Get All Labels</span>
                                </div>
                            )}
                            {canAction('bulkDeleteArticles') && (
                                <div
                                    className="action-tile"
                                    onClick={() => handleActionChange('bulkDeleteArticles')}
                                >
                                    <span className="action-tile-icon">🗑️</span>
                                    <span className="action-tile-label">Bulk Delete Articles</span>
                                </div>
                            )}
                            {canAction('bulkGrabPush') && (
                                <div
                                    className="action-tile"
                                    onClick={() => { handleModeChange('byStore'); handleActionChange('bulkGrabPush'); }}
                                >
                                    <span className="action-tile-icon">🔁</span>
                                    <span className="action-tile-label">Bulk Grab &amp; Push Store Info</span>
                                </div>
                            )}
                            {canAction('duplicateMacRemediation') && (
                                <div
                                    className="action-tile"
                                    onClick={() => handleActionChange('duplicateMacRemediation')}
                                >
                                    <span className="action-tile-icon">🧬</span>
                                    <span className="action-tile-label">Duplicate MAC Remediation</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Coming Soon */}
                    <div className="action-tiles-section">
                        <h2>Coming Soon</h2>
                        <div className="action-tiles-grid">
                            <div className="action-tile disabled">
                                <span className="action-tile-icon">🏪</span>
                                <span className="action-tile-label">Store Config</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Function Assignment Component - Use the full standalone component */}
            {currentAction === 'functionAssignment' && canAction('functionAssignment') && (
                <FunctionAssignment />
            )}

            {/* Bulk Grab & Push Store Info - standalone component */}
            {currentAction === 'bulkGrabPush' && canAction('bulkGrabPush') && (
                <BulkGrabPushStoreInfo />
            )}

            {/* All Companies / All Stores Summary - standalone component */}
            {currentAction === 'allStoresSummary' && canAction('allStoresSummary') && (
                <AllStoresSummary />
            )}

            {/* Duplicate MAC Remediation - standalone component */}
            {currentAction === 'duplicateMacRemediation' && canAction('duplicateMacRemediation') && (
                <DuplicateMacRemediation
                    globalCompany={globalCompany}
                    globalStores={globalStores}
                    globalSelectedStore={globalSelectedStore}
                />
            )}

            {/* Blink LED Configuration - v2 API with Store */}
            {currentAction === 'blinkLed' && canAction('blinkLed') && (
                <BlinkLedPanel
                    globalCompany={globalCompany}
                    globalSelectedStore={globalSelectedStore}
                    executing={executing}
                    setExecuting={setExecuting}
                    results={results}
                    setResults={setResults}
                    setErrors={setErrors}
                />
            )}

            {/* Bulk Delete Articles Configuration */}
            {currentAction === 'bulkDeleteArticles' && canAction('bulkDeleteArticles') && (
                <BulkDeleteArticlesPanel
                    executing={executing}
                    setExecuting={setExecuting}
                    results={results}
                    setResults={setResults}
                    setErrors={setErrors}
                />
            )}

            {/* Get All Labels Configuration */}
            {currentAction === 'getAllLabels' && canAction('getAllLabels') && (
                <GetAllLabelsPanel
                    globalCompany={globalCompany}
                    globalSelectedStore={globalSelectedStore}
                    results={results}
                    setResults={setResults}
                    errors={errors}
                    setErrors={setErrors}
                />
            )}

            {/* Assign Product Configuration */}
            {currentAction === 'assignProduct' && canAction('assignProduct') && (
                <AssignLabelPanel
                    globalCompany={globalCompany}
                    globalSelectedStore={globalSelectedStore}
                    executing={executing}
                    setExecuting={setExecuting}
                    setResults={setResults}
                    setErrors={setErrors}
                />
            )}

            {/* Regenerate Images Configuration */}
            {currentAction === 'regenerateImages' && canAction('regenerateImages') && (
                <RegenerateImagesPanel
                    globalCompany={globalCompany}
                    globalStores={globalStores}
                    executing={executing}
                    setExecuting={setExecuting}
                    results={results}
                    setResults={setResults}
                    setErrors={setErrors}
                />
            )}

            {/* Errors Display */}
            {errors.length > 0 && (
                <div className="errors-section">
                    <h3>⚠️ Errors</h3>
                    <div className="error-list">
                        {errors.map((error, index) => (
                            <div key={index} className="error-item">
                                {error}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Results Display - Page Flip */}
            {currentAction === 'pageFlip' && canAction('pageFlip') && (
                <PageFlipResults results={results} />
            )}

            {/* Results Display - Regenerate Images */}
            {currentAction === 'regenerateImages' && canAction('regenerateImages') && (
                <RegenerateImagesResults results={results} />
            )}

        </div>
        </ProtectedComponent>
    );
};

export default GlobalActionCenter;
